import { formatName } from '../lib/format';
import './ui/layout.css';
import Button from './ui/Button';
import React, { useEffect, useState } from "react";
import { LayoutDashboardIcon, BriefcaseIcon, BuildingIcon, ListChecksIcon, Building2Icon, UsersIcon, BoxesIcon, ClipboardListIcon, ClockIcon, FileTextIcon, ReceiptIcon, LogOutIcon, MenuIcon, XIcon } from './NavIcons';
import BrandLogo from './BrandLogo';
import ModalComponent from './Modal';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase-config";
import { useSelector } from "react-redux";
import { resolveStorageFileUrl } from "../storageUpload";
// The sidebar remounts on every page; keep resolved logo URLs for a while so the
// logo does not flicker or re-request on each navigation.
const LOGO_URL_TTL_MS = 30 * 60 * 1000
const resolvedLogoCache = new Map()

const Sidebar = () => {
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const userInfo = useSelector((state) => state.user.user)
    const activeOrgInfo = useSelector((state) => state.user.activeOrgInfo)
    const location = useLocation()

    const userName = formatName([userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ') || userInfo?.email || '')
    const companyLogo = activeOrgInfo?.logo
    const companyLogoSource = typeof companyLogo === 'string' ? companyLogo : companyLogo?.url || ''
    const logoCacheKey = companyLogoSource ? `${activeOrg?.id || ''}|${companyLogoSource}` : ''
    const cachedLogo = logoCacheKey ? resolvedLogoCache.get(logoCacheKey) : null
    const [companyLogoUrl, setCompanyLogoUrl] = useState(
        cachedLogo && Date.now() - cachedLogo.at < LOGO_URL_TTL_MS ? cachedLogo.url : ''
    )

    // New logo uploads store a storage path, not a web address, so it must be
    // resolved (signed) before an <img> can load it.
    useEffect(() => {
        if (!logoCacheKey) {
            setCompanyLogoUrl('')
            return undefined
        }
        const cached = resolvedLogoCache.get(logoCacheKey)
        if (cached && Date.now() - cached.at < LOGO_URL_TTL_MS) {
            setCompanyLogoUrl(cached.url)
            return undefined
        }
        let isCurrent = true
        const file = typeof companyLogo === 'string' ? { url: companyLogo } : companyLogo
        resolveStorageFileUrl(file, activeOrg?.id || '')
            .then(url => {
                if (url) resolvedLogoCache.set(logoCacheKey, { url, at: Date.now() })
                if (isCurrent) setCompanyLogoUrl(url || '')
            })
            .catch(() => { if (isCurrent) setCompanyLogoUrl('') })
        return () => { isCurrent = false }
        // companyLogo is represented by logoCacheKey (org + source).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoCacheKey])

    // Same behaviour as the old header's Sign out button.
    const signOutUser = async () => {
        await auth.signOut()
        window.location.reload()
    }


    const configSections = [
        { name: 'Dashboard', url: '/', icon: LayoutDashboardIcon },
        { name: 'Stats & Reports', url: '/stats-reports', icon: FileTextIcon },
        { name: 'Job Market', url: '/job-market', icon: BriefcaseIcon },
        { name: 'Allocations', url: '/allocations', icon: ClipboardListIcon },
        { name: 'Timesheet', url: '/timesheet', icon: ClockIcon },
        { name: 'Finance', url: '/invoices', icon: ReceiptIcon },
        { name: 'Tasks', url: '/tasks', icon: ListChecksIcon },
        { name: 'Clients', url: '/clients', icon: Building2Icon },
        { name: 'Site Hub', url: '/site-management', icon: BuildingIcon },
        { name: 'Client Hub', url: '/client-hub', icon: BriefcaseIcon },
        { name: 'Workers', url: '/workers', icon: UsersIcon, allowWorker: true },
        { name: 'Assets & Equipment', url: '/assets', icon: BoxesIcon },
        { name: 'Forms & Docs', url: '/forms', icon: FileTextIcon },
        { name: 'Company Profile', url: '/companyProfile', icon: BuildingIcon },
    ];

    const workerSections = [
        { name: 'Overview', url: '/worker/overview', icon: LayoutDashboardIcon },
        { name: 'Allocations', url: '/workers?view=allocations', icon: ClipboardListIcon },
        { name: 'Timesheets', url: '/worker/timesheets', icon: ClockIcon },
        { name: 'Invoices', url: '/worker/invoices', icon: ReceiptIcon },
        { name: 'Forms', url: '/worker/forms', icon: FileTextIcon },
        { name: 'My Profile', url: '/workers', icon: UsersIcon },
        { name: 'Company', url: '/worker/company', icon: BuildingIcon },
    ]

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)

    const navigation = useNavigate()
    const availableSections = activeOrg?.role === 'admin'
        ? configSections
        : activeOrg?.role === 'user'
            ? configSections.filter(section => section.name === 'Client Hub')
        // Client contacts only ever use Site Hub, limited to their own sites.
        : activeOrg?.role === 'client'
            ? configSections.filter(section => section.name === 'Site Hub')
        : activeOrg?.role === 'worker'
            ? workerSections
            : []

    useEffect(() => {
        setMobileMenuOpen(false)
    }, [location.pathname])

    useEffect(() => {
        document.body.classList.toggle('sidebarDrawerOpen', mobileMenuOpen)

        return () => {
            document.body.classList.remove('sidebarDrawerOpen')
        }
    }, [mobileMenuOpen])

    const renderNavItem = ({ name, url, icon: Icon }) => {
        const [urlPath, urlQuery = ''] = url.split('?')
        const currentParams = new URLSearchParams(location.search)
        // Match on the nav item's own params being present rather than an exact
        // search compare, so deep links carrying extra params still highlight.
        const active = urlQuery
            ? location.pathname === urlPath && [...new URLSearchParams(urlQuery)].every(([key, value]) => currentParams.get(key) === value)
            : location.pathname === urlPath && !(activeOrg?.role === 'worker' && url === '/workers' && currentParams.get('view') === 'allocations')
        if (url === '/forms') return <Link key={name} to={url} aria-current={active ? 'page' : undefined} className={active ? 'tabButtonActive hover portalSidebarNavButton' : 'tabButton hover sidebarDiv portalSidebarNavButton'} onClick={() => setMobileMenuOpen(false)}><Icon className='portalSidebarNavIcon' /><span className='portalSidebarNavLabel'>{name}</span></Link>
        return <button key={name} type='button' aria-current={active ? 'page' : undefined} className={active ? 'tabButtonActive hover portalSidebarNavButton' : 'tabButton hover sidebarDiv portalSidebarNavButton'} onClick={() => { setMobileMenuOpen(false); navigation(url) }}>
            <Icon className='portalSidebarNavIcon' data-icon-category={name === 'Allocations' ? 'allocations' : ['Timesheet', 'Timesheets'].includes(name) ? 'timesheets' : ['Invoices', 'Finance'].includes(name) ? 'invoices' : undefined} />
            <span className='portalSidebarNavLabel'>{name === 'Dashboard' ? 'Overview' : name === 'Timesheet' ? 'Timesheets' : name === 'Tasks' ? 'Tasks & Appointments' : name}</span>
        </button>
    }

    return <>
        <button
            type='button'
            className='portalSidebarMobileToggle hide-print'
            aria-label='Open navigation menu'
            onClick={() => setMobileMenuOpen(true)}
        >
            <MenuIcon />
            <span>Menu</span>
        </button>
        <button
            type='button'
            className={mobileMenuOpen ? 'portalSidebarBackdrop portalSidebarBackdropVisible' : 'portalSidebarBackdrop'}
            aria-label='Close navigation menu'
            onClick={() => setMobileMenuOpen(false)}
        />
        <div className={mobileMenuOpen ? 'tabBar tabBarMobileOpen' : 'tabBar'}>
            <div className='portalSidebarShell'>
                <div className='portalSidebarBrand'>
                    <div className='portalSidebarBrandText'>
                        {/* leading-none equivalent: keeps the slogan tucked under the wordmark. */}
                        <div className='portalSidebarTitle' style={{ lineHeight: 1 }}>
                            <BrandLogo height={24} />
                        </div>
                        <div className='portalSidebarSubtitle'>Simply managed.</div>
                    </div>
                    <button
                        type='button'
                        className='portalSidebarMobileClose'
                        aria-label='Close navigation menu'
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <XIcon />
                    </button>
                </div>
                <nav className='portalSidebarNav' aria-label='Main navigation'>
                    {availableSections.map(section => <React.Fragment key={section.name}>
                        {renderNavItem(section)}
                        {section.name === 'Company Profile' && <button type='button' className='tabButton hover sidebarDiv portalSidebarNavButton portalSidebarNavSignOut hide-print' onClick={() => setShowSignOutConfirm(true)}><LogOutIcon className='portalSidebarNavIcon' /><span className='portalSidebarNavLabel'>Sign Out</span></button>}
                    </React.Fragment>)}
                    {['worker', 'user', 'client'].includes(activeOrg?.role) && <button type='button' className='tabButton hover sidebarDiv portalSidebarNavButton portalSidebarNavSignOut hide-print' onClick={() => setShowSignOutConfirm(true)}><LogOutIcon className='portalSidebarNavIcon' /><span className='portalSidebarNavLabel'>Sign Out</span></button>}
                </nav>
                <div className='portalSidebarFooter'>
                    <div className='portalSidebarManagedBy'>
                        <div className='portalSidebarManagedByLabel'>Managd by:</div>
                        <div className='portalSidebarUserName'>{(formatName(userName) || <span className='uiAccountPlaceholder' aria-label='Loading account name' />)}</div>
                    </div>
                    {companyLogoUrl && (
                        <div className='portalSidebarCompanyLogo'>
                            <img src={companyLogoUrl} alt={`${activeOrgInfo?.name || 'Company'} logo`} />
                        </div>
                    )}
                </div>
            </div>
        </div>
        <ModalComponent
            showModal={showSignOutConfirm}
            setShowModal={setShowSignOutConfirm}
            customMaxWidth='300px'
            customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
        >
            <div className='allocationConfirmMessage recordConfirmMessage'>
                <h2 className='modalTitle'>Are you sure?</h2>
                <p className='modalSubtitle'>Sign out of your account?</p>
                <div className='modalFooter'>
                    <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setShowSignOutConfirm(false)}>Cancel</Button>
                    <Button variant="secondary" size="md" className='modalDangerButton' onClick={signOutUser}>Sign Out</Button>
                </div>
            </div>
        </ModalComponent>
    </>
}


export default Sidebar
